// bk22 O'rmon Bag'rida — paper-invitation preview card with a woodland
// forest-green frame. A botanical sage gradient wraps an inner cream
// card; the central photo medallion is ringed in fern-green, names
// appear in Cormorant Garamond, and the caption pairs date + venue.
import { Photo, type LayoutCtx } from "./_shared";

export function BookieForestPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const captionDate = formatDate(date);
  // Forest frame: deep evergreen washed with moss + soft sage stops.
  const FOREST_FRAME =
    "linear-gradient(135deg, #1F2D24 0%, #4F6B49 24%, #8FAE7E 46%, #4F6B49 66%, #1F2D24 100%)";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOREST_FRAME,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "4%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Paired fern fronds — small. */}
        <svg
          aria-hidden
          viewBox="0 0 120 18"
          style={{ width: "44%", height: "auto", marginBottom: "3%", color: accent, opacity: 0.85 }}
        >
          <g fill="currentColor" opacity="0.75">
            <ellipse cx="22" cy="9" rx="6" ry="2" transform="rotate(-26 22 9)" />
            <ellipse cx="34" cy="9" rx="5.5" ry="1.9" transform="rotate(-22 34 9)" />
            <ellipse cx="46" cy="9" rx="4.8" ry="1.7" transform="rotate(-18 46 9)" />
            <ellipse cx="98" cy="9" rx="6" ry="2" transform="rotate(26 98 9)" />
            <ellipse cx="86" cy="9" rx="5.5" ry="1.9" transform="rotate(22 86 9)" />
            <ellipse cx="74" cy="9" rx="4.8" ry="1.7" transform="rotate(18 74 9)" />
          </g>
          <circle cx="60" cy="9" r="1.6" fill={accent} />
        </svg>

        {/* Circular photo medallion with fern-green ring. */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOREST_FRAME,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 40%, ${accent}33, ${bg} 65%)`,
                      }}
                    />
                  }
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 55%, ${accent}22 100%)`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
            letterSpacing: "0.01em",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TO&apos;Y MAROSIMI · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${parseInt(m[1], 10)} ${months[parseInt(m[2], 10) - 1] ?? ""} ${m[3]}`;
}
